import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { FaTimes } from 'react-icons/fa';
import AlertContext from '../../../../../Context/Alert/AlertContext';
import { BaseURL } from '../../../../../Files/BaseURL';

function UploadDoc() {
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertContext);
  const [Files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle File Selection with Validation
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      // Add other acceptable MIME types as needed
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB per file

    const validatedFiles = selectedFiles.filter((file) => {
      if (!validTypes.includes(file.type)) {
        showAlert(`Unsupported file type: ${file.type}`, 'danger');
        return false;
      }
      if (file.size > maxSize) {
        showAlert(`File size exceeds 5MB: ${file.name}`, 'danger');
        return false;
      }
      return true;
    });

    setFiles((prevFiles) => [...prevFiles, ...validatedFiles]);
  };

  // Handle File Removal
  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Files.length === 0) {
      showAlert('Please upload at least one document.', 'warning');
      return;
    }
    if (Files.length > 5) {
      showAlert('You can upload up to 5 documents.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = localStorage.getItem('p@token');
      if (!authToken) {
        showAlert('Authentication token not found. Please log in.', 'danger');
        setIsSubmitting(false);
        return;
      }

      // Create FormData object
      const formData = new FormData();
      Files.forEach((file) => {
        formData.append('files', file);
      });

      // Make the PUT request
      const response = await fetch(`${BaseURL}/api/business/freelancesetup/freelancebsuinessupload`, {
        method: 'PUT',
        headers: {
          'auth-token': authToken,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message || 'Documents uploaded successfully!', 'success');
        navigate('/profileCreation/freelancer/plan');
      } else {
        showAlert(result.message || 'An error occurred while uploading documents.', 'danger');
      }
    } catch (error) {
      console.error('Error submitting the form:', error);
      showAlert('An unexpected error occurred. Please try again later.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Progress Bar */}
      <div className="bg-gray-100">
        <div className="w-full h-2 bg-primarygreen rounded-r-full"></div>
      </div>

      {/* Main Container */}
      <div className="flex justify-center items-center py-20">
        <div className="mx-6 w-full md:w-3/5 lg:w-2/5 bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-pop text-center font-bold mb-4">Upload Documents</h2>

          <div className="mb-4 flex flex-col gap-2">
            <p className="text-black font-light font-pop text-lg mb-2">
              Please upload all your business documents to verify your company. Missing documents may lead to a longer process or to decline your application.
            </p>

            {/* File Upload Section */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="dropzone-file"
                  className="flex flex-col items-center justify-center w-full border-2 border-primarygreen border-dashed rounded-lg cursor-pointer bg-transparent dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                >
                  <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                    <IoIosAddCircleOutline className="text-2xl" />
                    <p className="text-lg">Upload file</p>
                  </div>
                  {/* Allow multiple file selection */}
                  <input
                    id="dropzone-file"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf, .jpeg, .jpg, .png"
                  />
                </label>
              </div>

              {/* Display Selected Files */}
              {Files.length > 0 && (
                <div className="mt-4">
                  <ul className="space-y-2 flex gap-2 flex-wrap">
                    {Files.map((file, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-1 justify-between bg-gray-100 p-2 rounded"
                      >
                        <span className="text-gray-700">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                          aria-label={`Remove ${file.name}`}
                        >
                          <FaTimes />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`my-4 w-full bg-green-500 hover:bg-transparent hover:bg-white-500 font-semibold hover:text-black font-pop text-xl py-3 px-4 border border-black-500 hover:border-green-500 rounded-full text-white ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                } animate__animated animate__backInUp`}
              >
                {isSubmitting ? 'Uploading...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default UploadDoc;
